// Shared option builder for `pie` and `donut`. The two components differ
// only in their inner radius, and both have the same legend problem: a
// long category list wraps the legend into several rows that climb into
// the slices. Three things keep it readable:
//
//   1. Tail folding — beyond `palette.length + 1` categories, the
//      smallest slices fold into one neutral "Other" slice. Slices past
//      the palette repeat colours anyway, and the tail is too thin to
//      read, so the fold loses nothing a reader could see.
//   2. Side legend — on wide containers the legend becomes a vertical
//      list to the right of the pie, which fits far more labels than a
//      wrapped bottom strip. Long names truncate with a hover tooltip.
//   3. Scroll — the legend is `type: "scroll"` (from `legendDefaults`),
//      so if it still overflows it pages rather than overlapping.

import { formatValue } from "../format.ts";
import { estimateLegendRows } from "./chart-layout";
import {
  chartChrome,
  chartLabelColor,
  legendDefaults,
  tooltipDefaults,
  tooltipTextStyle,
} from "./echarts-base";

/** Container width at which the legend moves beside the pie. */
export const SIDE_LEGEND_MIN_WIDTH = 480;

/** Names that already mean "the rest" — folded into the tail slice. */
const OTHER_NAMES = new Set(["other", "others"]);

export interface PieSlice {
  name: string;
  value: number;
  /** Set on the folded tail slice: the categories it stands for. */
  members?: ReadonlyArray<{ name: string; value: number }>;
}

/**
 * Fold the tail of `slices` into a single "Other" slice once there are
 * more than `maxSlices`. Keeps the `maxSlices - 1` largest categories in
 * their original (author's ORDER BY) order and appends "Other" last.
 *
 * A category the data already calls "Other" is never kept on its own
 * when folding — it joins the tail, and its spelling becomes the
 * slice's label so the chart doesn't show "OTHER" and "Other" side by
 * side. Below the limit the input passes through unchanged.
 */
export function foldSlices(
  slices: ReadonlyArray<PieSlice>,
  maxSlices: number,
): PieSlice[] {
  if (slices.length <= maxSlices) return [...slices];

  const isOther = (s: PieSlice) =>
    OTHER_NAMES.has(s.name.trim().toLowerCase());
  const magnitude = (s: PieSlice) =>
    Number.isFinite(s.value) && s.value > 0 ? s.value : 0;

  const keep = new Set(
    slices
      .map((s, i) => ({ s, i }))
      .filter(({ s }) => !isOther(s))
      .sort((a, b) => magnitude(b.s) - magnitude(a.s) || a.i - b.i)
      .slice(0, maxSlices - 1)
      .map(({ i }) => i),
  );

  const kept: PieSlice[] = [];
  const members: Array<{ name: string; value: number }> = [];
  let label = "Other";
  slices.forEach((s, i) => {
    if (keep.has(i)) {
      kept.push(s);
      return;
    }
    if (isOther(s)) label = s.name;
    for (const m of s.members ?? [s]) members.push({ name: m.name, value: m.value });
  });
  members.sort((a, b) => magnitude(b) - magnitude(a));

  const total = members.reduce((sum, m) => sum + magnitude(m), 0);
  return [...kept, { name: label, value: total, members }];
}

const escapeHtml = (s: string) =>
  s.replace(
    /[&<>"']/g,
    (c) =>
      ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" })[c]!,
  );

/** Members listed in the "Other" tooltip before collapsing to "+ n more". */
const OTHER_TOOLTIP_ROWS = 8;

function otherTooltip(slice: PieSlice, format: string | undefined) {
  return (p: { marker?: string; percent?: number }) => {
    const members = slice.members ?? [];
    const rows = members
      .slice(0, OTHER_TOOLTIP_ROWS)
      .map((m) => `${escapeHtml(m.name)}: ${escapeHtml(formatValue(m.value, format))}`);
    if (members.length > OTHER_TOOLTIP_ROWS) {
      rows.push(`+ ${members.length - OTHER_TOOLTIP_ROWS} more`);
    }
    const head =
      `${p.marker ?? ""}<b>${escapeHtml(slice.name)}</b>: ` +
      `${escapeHtml(formatValue(slice.value, format))} (${p.percent ?? 0}%)`;
    return [head, ...rows].join("<br/>");
  };
}

export interface PieOptionInput {
  rows: ReadonlyArray<Record<string, unknown>>;
  category: string;
  value: string;
  /** Format string for `value`, used by the "Other" tooltip. */
  format?: string;
  /** `"70%"` for a pie, `[inner, "70%"]` for a donut. */
  radius: string | [string, string];
  /** Container width in px; 0 before the first measure. */
  width: number;
  /** Container height in px. */
  height: number;
  print: boolean;
}

export function pieOption(input: PieOptionInput) {
  const chrome = chartChrome({ print: input.print });
  const palette = chrome.color;
  const slices = foldSlices(
    input.rows.map((r) => ({
      name: String(r[input.category] ?? ""),
      value: Number(r[input.value]),
    })),
    palette.length + 1,
  );

  const legend = legendDefaults({ print: input.print });
  const side = input.width >= SIDE_LEGEND_MIN_WIDTH;

  let center: [string, string];
  let legendPlacement: Record<string, unknown>;
  if (side) {
    center = ["30%", "50%"];
    legendPlacement = {
      orient: "vertical",
      left: "58%",
      right: 8,
      top: "middle",
      bottom: undefined,
      textStyle: {
        ...legend.textStyle,
        // Marker (10) + marker gap (5) + right margin (8) come out of
        // the 42% column the legend owns.
        width: Math.max(60, Math.floor(input.width * 0.42) - 23),
        overflow: "truncate",
      },
      // Full name on hover when truncated.
      tooltip: { show: true, textStyle: { ...tooltipTextStyle() } },
    };
  } else {
    // A scroll legend is always one row. Print can't scroll, so the
    // legend wraps there — lift the pie by the rows it will take.
    const rows =
      input.print && input.width > 0
        ? estimateLegendRows(
            slices.map((s) => legend.formatter(s.name)),
            input.width,
          )
        : 1;
    const reserved = rows * 22;
    const plotHeight = Math.max(input.height - reserved, input.height * 0.5);
    center = ["50%", `${Math.round((plotHeight / 2 / input.height) * 100)}%`];
    legendPlacement = { top: undefined, bottom: 0 };
  }

  // Radius is relative to min(width, height) / 2; shrink it when the
  // bottom legend eats into the height so the pie stays clear of it.
  const centerY = parseFloat(center[1]) / 100;
  const outerPct = side ? 70 : Math.min(70, Math.round(centerY * 2 * 80));
  const outer = `${outerPct}%`;
  const radius = Array.isArray(input.radius)
    ? [input.radius[0], outer]
    : outer;

  return {
    ...chrome,
    // Confined: the "Other" member list (up to 9 lines) otherwise runs
    // off a phone screen.
    tooltip: tooltipDefaults("item", { width: input.width }),
    legend: { ...legend, ...legendPlacement },
    series: [
      {
        type: "pie" as const,
        center,
        radius,
        avoidLabelOverlap: true,
        // The legend always carries the names (folding caps the count),
        // so slice labels would only duplicate it.
        label: { show: false },
        labelLine: { show: false },
        data: slices.map((s, i) => ({
          name: s.name,
          value: s.value,
          itemStyle: {
            color: s.members ? chartLabelColor() : palette[i % palette.length],
          },
          ...(s.members
            ? { folded: true, tooltip: { formatter: otherTooltip(s, input.format) } }
            : {}),
        })),
      },
    ],
  };
}
